import { useMutation, useQuery } from '@tanstack/react-query';
import { Gauge, RefreshCw } from 'lucide-react';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../../components/ui';

interface SpeedProfile {
  name: string;
  direction: 'up' | 'down';
  fixedKbps: number | null;
  assuredKbps: number | null;
  maxKbps: number | null;
  boundCount: number | null;
  /** True where the chassis's profile has no direction and the name supplies it. */
  bidirectional?: boolean;
  /** `chassis` was read back just now; `recorded` is our log of applying it. */
  source?: 'chassis' | 'recorded';
  appliedAt?: number | null;
  appliedBy?: string | null;
}

interface SpeedProfilesResponse {
  data?: { items: SpeedProfile[]; warnings: string[] };
  items?: SpeedProfile[];
  warnings?: string[];
  fetchedAt?: number;
}

/** kbps as an operator says it: 1048064 -> "1G", 56320 -> "55M". */
function speed(kbps: number | null): string {
  if (kbps === null || kbps <= 0) return '—';
  if (kbps >= 1_000_000) {
    const gig = kbps / 1_048_576;
    return `${gig >= 0.95 && gig <= 1.05 ? 1 : Math.round(gig * 10) / 10}G`;
  }
  return `${Math.round(kbps / 1024)}M`;
}

/**
 * The bandwidth profiles this chassis holds.
 *
 * Read from the OLT rather than kept here, because they already exist there
 * and a second list would drift from the one that actually shapes traffic.
 * Read-only for now: creating a profile is a chassis write whose syntax has not
 * been verified on hardware, and this codebase does not ship write paths it has
 * not watched work.
 */
export function SpeedProfilesTab({ oltId }: { oltId: string }) {
  const query = useQuery({
    queryKey: ['olt', oltId, 'speed-profiles'],
    queryFn: () =>
      api.get<SpeedProfilesResponse>(`/olts/${encodeURIComponent(oltId)}/speed-profiles`),
  });

  // The refresh reads the chassis live (multi-second); a bare promise gave the
  // button no in-flight state and swallowed a refused/timed-out chassis, so the
  // operator re-pressed and opened a second CLI session. A mutation carries both.
  const reread = useMutation({
    mutationFn: () =>
      api.get(`/olts/${encodeURIComponent(oltId)}/speed-profiles?refresh=1`),
    onSuccess: () => query.refetch(),
  });

  const payload = query.data?.data ?? query.data;
  const items = payload?.items ?? [];
  const warnings = payload?.warnings ?? [];
  const fetchedAt = query.data?.fetchedAt;

  const up = items.filter((p) => p.direction === 'up');
  const down = items.filter((p) => p.direction === 'down');

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Gauge className="size-4 text-muted" />
          Speed profiles
          <span className="text-xs font-normal text-faint">{items.length}</span>
        </span>
      }
      action={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw className="size-3.5" />}
          loading={reread.isPending || query.isFetching}
          onClick={() => reread.mutate()}
        >
          Re-read
        </Button>
      }
    >
      <p className="border-b border-line px-5 py-2 text-xs text-faint">
        Read from the chassis, which already holds them — a second list kept here would drift from
        the one that actually shapes traffic.
        {fetchedAt ? ` Last read ${formatRelative(fetchedAt)}.` : ''}
      </p>

      {reread.error ? (
        <div className="px-5 pt-3">
          <ErrorNotice error={reread.error} />
        </div>
      ) : null}

      {query.isLoading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : query.error ? (
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Gauge className="size-8" />}
          title="No profiles read yet"
          description="Re-read to ask the chassis for its bandwidth profiles."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">Profile</th>
            <th className="th">Direction</th>
            <th className="th">Ceiling</th>
            <th className="th">Assured</th>
            <th className="th">Fixed</th>
            <th className="th">ONUs using it</th>
            <th className="th">Known from</th>
          </THead>
          <TBody>
            {[...up, ...down].map((p) => (
              <TRow key={`${p.direction}-${p.name}`}>
                <td className="td font-mono text-xs font-medium text-ink">{p.name}</td>
                <td className="td">
                  <Pill tone={p.direction === 'up' ? 'primary' : 'info'}>
                    {p.direction === 'up' ? 'upstream' : 'downstream'}
                  </Pill>
                  {p.bidirectional ? (
                    <span
                      className="ml-1.5 text-faint"
                      title="This chassis binds one profile either way — the direction shown is read from the name"
                    >
                      either way
                    </span>
                  ) : null}
                </td>
                <td className="td font-medium tabular-nums">{speed(p.maxKbps)}</td>
                <td className="td tabular-nums text-muted">{speed(p.assuredKbps)}</td>
                <td className="td tabular-nums text-muted">{speed(p.fixedKbps)}</td>
                <td className="td tabular-nums text-muted">
                  {p.boundCount === null ? (
                    <span className="text-faint" title="This chassis does not state it">
                      —
                    </span>
                  ) : (
                    p.boundCount
                  )}
                </td>
                <td className="td text-xs">
                  {p.source === 'recorded' ? (
                    <span
                      title={
                        p.appliedBy
                          ? `Applied by ${p.appliedBy}; the chassis did not list it in this read`
                          : 'The chassis did not list it in this read'
                      }
                    >
                      <Pill tone="warn">our record</Pill>
                    </span>
                  ) : (
                    <span className="text-muted">the chassis</span>
                  )}
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}

      {warnings.map((w) => (
        <p key={w} className="border-t border-line px-5 py-2 text-xs text-warn">
          {w}
        </p>
      ))}
    </Card>
  );
}
